import React from 'react';
import FallbackImage from '../../components/FallbackImage/FallbackImage';

const SustainabilityPage = () => {
  return (
    <div className="sustainability-page min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0B1E2B] to-[#1a3344] text-white pt-28 md:pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Zenvia Foods | Sustainability at Our Core
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              A Purpose-Driven Vision
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Purpose-Driven Vision */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B1E2B] mb-8">
                A Purpose-Driven Vision
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
                At Zenvia Foods, sustainability isn't just part of what we do—it guides everything we are. We believe that being responsible stewards of the earth goes hand in hand with supporting farmers, enriching communities, and producing food in ways that endure.
              </p>
            </div>
          </div>

          {/* Four Pillars of Action */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
            <h3 className="text-3xl font-bold text-[#0B1E2B] mb-8 text-center">
              Four Pillars of Action
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-6 bg-[#F3F4F6] rounded-xl">
                <div className="text-4xl mb-4">🌍</div>
                <h4 className="text-xl font-bold text-[#0B1E2B] mb-3">Climate</h4>
                <p className="text-gray-600">We are making every effort to reduce our carbon footprint—from sourcing inputs through to processing and distribution. Renewable energy, energy-efficient operations, and lowering emissions are central to our strategy.</p>
              </div>
              <div className="p-6 bg-[#F3F4F6] rounded-xl">
                <div className="text-4xl mb-4">🌿</div>
                <h4 className="text-xl font-bold text-[#0B1E2B] mb-3">Nature</h4>
                <p className="text-gray-600">We protect and regenerate the natural systems that our business depends on. This means organic farming methods, soil health, water conservation, and ensuring biodiversity across our supply chain.</p>
              </div>
              <div className="p-6 bg-[#F3F4F6] rounded-xl">
                <div className="text-4xl mb-4">♻️</div>
                <h4 className="text-xl font-bold text-[#0B1E2B] mb-3">Circularity & Waste</h4>
                <p className="text-gray-600">Our goal is zero waste. We turn fruit by-products and other by-wastes into valuable materials. Recycling, reusing, and redesigning processes are standard practices at every level of our operations.</p>
              </div>
              <div className="p-6 bg-[#F3F4F6] rounded-xl">
                <div className="text-4xl mb-4">🤝</div>
                <h4 className="text-xl font-bold text-[#0B1E2B] mb-3">Livelihoods & Partnerships</h4>
                <p className="text-gray-600">Our relationships with farmers are the backbone of what we do. We ensure fair trade, equitable income, and capacity building. By collaborating closely with local farming communities, we foster resilience, knowledge sharing, and shared success.</p>
              </div>
            </div>
          </div>

          {/* How We're Doing It */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
            <h3 className="text-3xl font-bold text-[#0B1E2B] mb-8 text-center">
              How We're Doing It
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-[#F3F4F6] rounded-xl">
                <div className="text-4xl mb-4">🎯</div>
                <h4 className="text-xl font-bold text-[#0B1E2B] mb-3">Strategic Integration</h4>
                <p className="text-gray-600">Embedding sustainability in leadership decision-making and corporate strategy.</p>
              </div>
              <div className="text-center p-6 bg-[#F3F4F6] rounded-xl">
                <div className="text-4xl mb-4">📊</div>
                <h4 className="text-xl font-bold text-[#0B1E2B] mb-3">Measurable Targets</h4>
                <p className="text-gray-600">Setting measurable targets to track our progress across climate, nature, waste, and community impact.</p>
              </div>
              <div className="text-center p-6 bg-[#F3F4F6] rounded-xl">
                <div className="text-4xl mb-4">🌐</div>
                <h4 className="text-xl font-bold text-[#0B1E2B] mb-3">Systemic Change</h4>
                <p className="text-gray-600">Advocating for systemic change: engaging with regulators, trade associations, and industry to support fair, sustainable, and ethical supply chains.</p>
              </div>
            </div>
          </div>

          {/* Our Commitment Going Forward */}
          <div className="bg-gradient-to-r from-[#FF6B35] to-[#e85a2b] rounded-2xl p-8 text-white mb-16">
            <h3 className="text-3xl font-bold mb-6 text-center">
              Our Commitment Going Forward
            </h3>
            <p className="text-lg leading-relaxed text-center max-w-4xl mx-auto opacity-90">
              We at Zenvia Foods strive to be recognized as an industry leader in environmental responsibility. We will continue to invest, innovate, and collaborate—transforming not just what we make, but how we make it—so that our growth nourishes people, communities, and the planet alike.
            </p>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-[#FF6B35] to-[#e85a2b] rounded-2xl p-8 text-white">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Join Our Sustainability Mission
              </h3>
              <p className="text-lg mb-6 opacity-90">
                Together, we can create a more sustainable future for the food industry
              </p>
              <a 
                href="/contact-us" 
                className="inline-block bg-white text-[#FF6B35] font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-300"
              >
                Partner With Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SustainabilityPage;
